import { useEffect, useState } from "react";
import { Loader2, Plus, Save } from "lucide-react";
import { toast } from "sonner";
import { api, errText } from "../../lib/api";
import { INFO_GRID } from "../../lib/infoBoxes";
import { InfoBox } from "../../components/home/InfoBoxes";
import { Switch } from "../../components/ui/switch";
import { Button } from "../../components/ui/button";
import { InfoBoxEditor } from "./InfoBoxEditor";

const BLANK = { icon: "shield", title_en: "", title_fr: "", body_en: "", body_fr: "", button_en: "", button_fr: "", button_link: "", icon_image: "", bg_image: "" };

export function InfoBoxesSettings() {
  const [d, setD] = useState(null);
  const [saving, setSaving] = useState(false);
  const [lang, setLang] = useState("en");
  useEffect(() => { api.get("/admin/site/info-boxes").then(({ data }) => setD(data)).catch((e) => toast.error(errText(e))); }, []);
  if (!d) return <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-slate-300" /></div>;

  const boxes = d.boxes;
  const setBoxes = (bx) => setD({ ...d, boxes: bx, count: Math.min(d.count, Math.max(1, bx.length)) });
  const move = (i, dir) => { const bx = [...boxes]; [bx[i], bx[i + dir]] = [bx[i + dir], bx[i]]; setBoxes(bx); };
  const shown = boxes.slice(0, d.count);
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/site/info-boxes", { enabled: d.enabled, count: d.count, boxes });
      setD({ ...d, ...data });
      toast.success("Info boxes saved");
    } catch (e) { toast.error(errText(e)); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-5" data-testid="info-boxes-settings">
      <div className="rounded-2xl border border-slate-200 bg-white p-5 flex flex-wrap items-center gap-x-8 gap-y-4">
        <label className="flex items-center gap-3"><Switch checked={d.enabled} onCheckedChange={(v) => setD({ ...d, enabled: v })} data-testid="info-boxes-enabled" />
          <span className="text-sm font-semibold text-[#12234A]">Show info boxes on the homepage</span></label>
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-[#12234A]">Boxes shown</span>
          {[1, 2, 3, 4, 5].map((n) => (
            <button key={n} type="button" disabled={n > boxes.length} onClick={() => setD({ ...d, count: n })} data-testid={`info-boxes-count-${n}`}
              className={`h-9 w-9 rounded-lg text-sm font-bold transition-colors disabled:opacity-30 ${d.count === n ? "bg-[#F5551E] text-white" : "bg-slate-100 text-[#12234A] hover:bg-slate-200"}`}>{n}</button>
          ))}
        </div>
        <Button onClick={save} disabled={saving} className="ml-auto rounded-xl bg-[#F5551E] hover:bg-[#E04812] text-white gap-2" data-testid="info-boxes-save">
          {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save
        </Button>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5" data-testid="info-boxes-preview">
        <div className="flex items-center justify-between mb-4">
          <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Live preview {!d.enabled && "· hidden on homepage"}</p>
          <div className="flex gap-1">{["en", "fr"].map((l) => (
            <button key={l} type="button" onClick={() => setLang(l)} data-testid={`info-boxes-preview-${l}`}
              className={`h-7 px-3 rounded-lg text-xs font-bold ${lang === l ? "bg-[#12234A] text-white" : "bg-white text-slate-500 border"}`}>{l.toUpperCase()}</button>
          ))}</div>
        </div>
        {shown.length ? (
          <div className={`grid gap-4 ${INFO_GRID[shown.length]} ${shown.length === 1 ? "mx-auto" : ""}`}>
            {shown.map((b, i) => <InfoBox key={b.id || i} b={b} lang={lang} testid={`info-box-preview-${i}`} />)}
          </div>
        ) : <p className="text-sm text-slate-400 text-center py-6">Add a box to get started.</p>}
      </div>

      {boxes.map((b, i) => (
        <InfoBoxEditor key={b.id || i} b={b} i={i} total={boxes.length} hidden={i >= d.count}
          onChange={(nb) => setBoxes(boxes.map((x, j) => (j === i ? nb : x)))}
          onMove={(dir) => move(i, dir)} onRemove={() => setBoxes(boxes.filter((_, j) => j !== i))} />
      ))}
      {boxes.length < d.max && (
        <button type="button" onClick={() => setD({ ...d, boxes: [...boxes, { ...BLANK, id: Math.random().toString(36).slice(2, 10) }] })} data-testid="info-boxes-add"
          className="w-full h-14 rounded-2xl border-2 border-dashed border-slate-300 text-sm font-semibold text-slate-500 hover:border-[#F5551E] hover:text-[#F5551E] inline-flex items-center justify-center gap-2 transition-colors">
          <Plus size={16} /> Add a box ({boxes.length}/{d.max})
        </button>
      )}
    </div>
  );
}
